// Generic list cache store: TTL + LRU + optional localStorage persistence,
// exposed to React through subscribe/peek (useSyncExternalStore-compatible).
// Modeled on syncQueueStore's module-store pattern.

import {
  loadNamespace,
  saveNamespace,
  removeNamespace,
} from './listCachePersistence';

export const LIST_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour
export const DEFAULT_MAX_ENTRIES = 20;

const registry = new Set();

/**
 * @param {{ namespace: string, ttlMs?: number, maxEntries?: number, persist?: boolean }} config
 */
export function createListCache({
  namespace,
  ttlMs = LIST_CACHE_TTL_MS,
  maxEntries = DEFAULT_MAX_ENTRIES,
  persist = false,
}) {
  const entries = new Map(); // key -> { data, ts }
  const listeners = new Set();

  if (persist) {
    const restored = loadNamespace(namespace, ttlMs);
    if (restored) restored.forEach(([key, entry]) => entries.set(key, entry));
  }

  function notify() {
    if (persist) saveNamespace(namespace, [...entries]);
    listeners.forEach((l) => l());
  }

  function isEntryFresh(entry) {
    return Boolean(entry) && Date.now() - entry.ts < ttlMs;
  }

  const cache = {
    namespace,
    ttlMs,

    /** Read without LRU touch or purge — stable reference, safe for getSnapshot. */
    peek(key) {
      return entries.get(key);
    },

    /** Read with LRU touch; undefined when missing or TTL-expired (lazy purge). */
    get(key) {
      const entry = entries.get(key);
      if (!entry) return undefined;
      if (!isEntryFresh(entry)) {
        entries.delete(key);
        notify();
        return undefined;
      }
      entries.delete(key);
      entries.set(key, entry);
      return entry.data;
    },

    set(key, data) {
      entries.delete(key);
      entries.set(key, { data, ts: Date.now() });
      while (entries.size > maxEntries) {
        entries.delete(entries.keys().next().value);
      }
      notify();
    },

    /** Functional update of an existing entry's data; keeps the original ts. */
    mutateEntry(key, updater) {
      const entry = entries.get(key);
      if (!entry) return;
      entries.set(key, { data: updater(entry.data), ts: entry.ts });
      notify();
    },

    remove(key) {
      if (entries.delete(key)) notify();
    },

    clear() {
      if (entries.size === 0) return;
      entries.clear();
      if (persist) removeNamespace(namespace);
      listeners.forEach((l) => l());
    },

    isFresh(key) {
      return isEntryFresh(entries.get(key));
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };

  registry.add(cache);
  return cache;
}

/** Wipe every registered cache — call on logout to avoid cross-account leakage. */
export function clearAllListCaches() {
  registry.forEach((cache) => cache.clear());
}
